// SPDX-License-Identifier: AGPL-3.0-or-later
// Domain jargon, defined for someone who has never encountered any of it.
// Rendered as click-to-open popovers via [data-term] spans.
export interface Term { term: string; definition: string }

export const GLOSSARY: Record<string, Term> = {
  register: {
    term: 'Register of Foreign Ownership',
    definition:
      'A live database the Australian Taxation Office runs, in which foreign persons must register interests they hold in Australian assets — agricultural land, water entitlements, residential and commercial land, business interests, and mining or exploration tenements. It began on 1 July 2023, merging older separate registers, and the ATO reports on it once a year (as at 30 June).',
  },
  foreignPerson: {
    term: 'Foreign person',
    definition:
      'Broadly, an individual who is not ordinarily resident in Australia, or a company or trust in which foreign interests hold a substantial stake (generally 20% or more from one country, or 40% from several). Because the threshold is a stake, land counted here can still have a large Australian ownership share in the same parcel.',
  },
  sourceCountry: {
    term: 'Source country (country of control)',
    definition:
      'Which country an interest is attributed to. The ATO does not use the country a company is incorporated in — it uses the nationality of the person or persons who appear to control the entity, or (for widely-held funds) where the directors, fund manager or listing sit. This is why investment-fund domiciles such as the Cayman Islands, Bermuda and the British Virgin Islands appear prominently: they are where the controlling manager is based, not necessarily where the ultimate money is from.',
  },
  agLand: {
    term: 'Agricultural land',
    definition:
      'Land used, or that could reasonably be used, for a primary-production business. Foreign ownership is measured in hectares of registered interest and compared with the ABS benchmark of total Australian agricultural land (387.265 million hectares, the 2020–21 Agricultural Census) to give a "level of foreign ownership" percentage.',
  },
  waterEntitlement: {
    term: 'Water entitlement',
    definition:
      'A right to take a share of water from a water resource (for example, an irrigation allocation), measured in megalitres (ML) or gigalitres (GL; 1 GL = 1,000 ML). Water entitlements are traded separately from land, so foreign ownership of water is registered separately from farmland.',
  },
  levelOfOwnership: {
    term: 'Level of foreign ownership',
    definition:
      'The registered foreign-held interest as a share of the national total — for farmland, hectares of registered interest divided by total Australian agricultural land. At 30 June 2025 it was about 13% for farmland and 12.7% for water entitlements. It has been broadly stable for a decade, not rising.',
  },
  registeredInterest: {
    term: 'Registered interest',
    definition:
      'A single registration of a foreign interest in an asset. The same parcel can carry more than one registered interest (for example a freehold and a leasehold holder, or several part-owners from different countries), so registered-interest counts and areas can exceed the number of distinct parcels.',
  },
  residentialFlow: {
    term: 'Residential acquisitions',
    definition:
      'Unlike the other classes, residential figures count new acquisitions made during each year (a flow), not a running total. Foreign non-residents are generally only permitted to buy new dwellings or vacant land, not established homes — which is why "new property" dominates the numbers.',
  },
  tenement: {
    term: 'Mining or exploration tenement',
    definition:
      'A licence or lease granting the right to explore for or extract minerals, petroleum or gas from a defined area. Foreign ownership here is counted as the number of registered tenement interests, not land area. State-level figures are heavily suppressed because there are so few holders.',
  },
  suppression: {
    term: 'Suppressed / “≤5”',
    definition:
      'To protect the privacy of individual registrants, the ATO withholds cells covering very small numbers, shown as "≤5" or left blank. A suppressed cell is a value of one to five — it is NOT zero. On this site suppressed values are shown as "not published" and left out of totals rather than treated as zero.',
  },
  benchmark: {
    term: 'ABS benchmark',
    definition:
      'The denominator used to turn a hectare or megalitre figure into a percentage — total Australian agricultural land or total water entitlements on issue, published by the Australian Bureau of Statistics and the Bureau of Meteorology. For farmland the ATO now uses a fixed 2020–21 Census figure so year-to-year percentages are comparable.',
  },
};

/** Wrap a term in an info-icon trigger. `key` must exist in GLOSSARY. */
export function gloss(key: string, label?: string): string {
  const t = GLOSSARY[key];
  if (!t) return label ?? key;
  return `<span class="glossary-link" data-term="${key}" tabindex="0" role="button" aria-label="Definition of ${t.term}">${label ?? t.term}<span class="gloss-icon" aria-hidden="true">i</span></span>`;
}

let popover: HTMLDivElement | null = null;
function ensurePopover(): HTMLDivElement {
  if (!popover) {
    popover = document.createElement('div');
    popover.className = 'glossary-popover';
    popover.setAttribute('role', 'dialog');
    document.body.appendChild(popover);
  }
  return popover;
}
export function hideGlossary(): void {
  popover?.classList.remove('visible');
}
function show(trigger: Element): void {
  const key = trigger.getAttribute('data-term') ?? '';
  const t = GLOSSARY[key];
  if (!t) return;
  const el = ensurePopover();
  el.innerHTML = `<h4></h4><p></p>`;
  (el.querySelector('h4') as HTMLElement).textContent = t.term;
  (el.querySelector('p') as HTMLElement).textContent = t.definition;
  el.classList.add('visible');
  const r = trigger.getBoundingClientRect();
  const pr = el.getBoundingClientRect();
  let left = r.left;
  let top = r.bottom + 8;
  if (left + pr.width > window.innerWidth - 12) left = window.innerWidth - pr.width - 12;
  if (top + pr.height > window.innerHeight - 12) top = r.top - pr.height - 8;
  el.style.left = `${Math.max(12, left)}px`;
  el.style.top = `${Math.max(12, top)}px`;
}
export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const trigger = (e.target as Element).closest('.glossary-link');
    if (trigger) { e.stopPropagation(); show(trigger); return; }
    if (!(e.target as Element).closest('.glossary-popover')) hideGlossary();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideGlossary();
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as Element)?.classList?.contains('glossary-link')) {
      e.preventDefault();
      show(e.target as Element);
    }
  });
  window.addEventListener('resize', hideGlossary);
}
